<script setup lang="ts">
// First-visit notice about the WeChat mini program (production assets/js/miniprogram-notice.js and
// assets/css/miniprogram-notice.css). Shown once per browser, never inside WeChat, never on the quiz,
// me and membership screens, and not while a login dialog or the support dialog is open.
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { overlays } from '../stores/overlays'
import { shellInfo } from '../shell/nav'
import { isDesktop } from '../shell/useShell'

const STORAGE_KEY = 'jx-mp-notice-v1'
const POSTER = '/assets/img/ui/miniprogram-launch-poster-v1.webp'
const SHOW_DELAY = 900
const SKIP_VIEWS = ['quiz', 'me', 'membership']

const route = useRoute()
const mounted = ref(false)
const shown = ref(false)
const card = ref<HTMLElement | null>(null)
let previousFocus: Element | null = null
const timers: number[] = []

function dismissed() {
  try { return localStorage.getItem(STORAGE_KEY) === '1' } catch { return false }
}
// The phone client (mobile.html) does not load the notice; it is a desktop-web popup.
const blocked = () => !isDesktop.value || dismissed() || /MicroMessenger/i.test(navigator.userAgent) || SKIP_VIEWS.includes(shellInfo(route.path).view)
  || Boolean(overlays.auth) || overlays.contact

function open() {
  if (mounted.value || blocked()) return false
  previousFocus = document.activeElement
  mounted.value = true
  document.body.classList.add('mp-notice-open')
  window.addEventListener('keydown', onKeydown)
  requestAnimationFrame(() => { shown.value = true })
  timers.push(window.setTimeout(() => { shown.value = true }, 120))
  void nextTick(() => card.value?.querySelector<HTMLElement>('.mp-notice-close')?.focus({ preventScroll: true }))
  return true
}
function close() {
  if (!mounted.value) return
  shown.value = false
  document.body.classList.remove('mp-notice-open')
  window.removeEventListener('keydown', onKeydown)
  try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* the notice may show again next visit */ }
  timers.push(window.setTimeout(() => { mounted.value = false }, 260))
  if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus()
}
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') { event.preventDefault(); close(); return }
  if (event.key !== 'Tab') return
  const items = [...(card.value?.querySelectorAll<HTMLElement>('button,a') ?? [])]
  const first = items[0], last = items[items.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}

onMounted(() => {
  if (dismissed() || /MicroMessenger/i.test(navigator.userAgent)) return
  let attempts = 0
  const poll = window.setInterval(() => {
    if (mounted.value || dismissed()) { window.clearInterval(poll); return }
    attempts++
    if (attempts >= 10) { window.clearInterval(poll); return }
    if (open()) window.clearInterval(poll)
  }, 3000)
  timers.push(poll)
  timers.push(window.setTimeout(open, SHOW_DELAY))
})
onBeforeUnmount(() => {
  timers.forEach(id => { window.clearTimeout(id); window.clearInterval(id) })
  document.body.classList.remove('mp-notice-open')
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div v-if="mounted" class="mp-notice-overlay" :class="{ show: shown }" @click.self="close">
    <section ref="card" class="mp-notice-card" role="dialog" aria-modal="true" aria-labelledby="mpNoticeTitle">
      <button class="mp-notice-close" type="button" aria-label="关闭公告" @click="close">×</button>
      <a class="mp-notice-poster-link" :href="POSTER" target="_blank" rel="noopener" aria-label="查看小程序海报大图">
        <img class="mp-notice-poster" :src="POSTER" width="1200" height="1200" alt="导游学霸微信小程序上线海报：扫海报上的小程序码，或在微信搜索「导游学霸」" loading="lazy" decoding="async">
      </a>
      <div class="mp-notice-body">
        <span class="mp-notice-kicker">NEW · 微信小程序</span>
        <h2 id="mpNoticeTitle" class="mp-notice-title">「导游学霸」小程序上线啦</h2>
        <p class="mp-notice-desc">微信扫一扫海报上的小程序码，或搜索 <b>「导游学霸」</b>，手机上随时随地刷题。</p>
        <div class="mp-notice-actions">
          <a class="mp-notice-secondary" :href="POSTER" download="导游学霸-小程序海报.webp">保存海报</a>
          <button class="mp-notice-primary" type="button" @click="close">我知道了</button>
        </div>
      </div>
    </section>
  </div>
</template>
